import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../service/auth.service';
import { RateLimitService } from '../../../service/rate-limit.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm: FormGroup;
  loading = false;
  checkingLogin = false;
  passwordVisible = false;
  passwordStrength = {
    score: 0,
    feedback: '',
    color: ''
  };

  private redirectUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService,
    private rateLimitService: RateLimitService
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]]
    });

    // Watch password changes for strength calculation
    this.loginForm.get('password')?.valueChanges.subscribe(value => {
      this.calculatePasswordStrength(value);
    });

    // Check for redirect URL in query params
    this.router.routerState.root.queryParams.subscribe(params => {
      this.redirectUrl = params['redirect'] || null;
    });
  }

  async onSubmit() {
    if (this.loginForm.valid) {
      if (this.rateLimitService.isLockedOut()) {
        const remainingTime = Math.ceil(this.rateLimitService.getRemainingLockoutTime() / 1000 / 60);
        this.messageService.add({
          severity: 'error',
          summary: 'Too Many Attempts',
          detail: `Too many failed login attempts. Please try again in ${remainingTime} minutes.`
        });
        return;
      }

      this.loading = true;
      const { username, password } = this.loginForm.value;

      try {
        // First, call check-login API to determine user status
        this.checkingLogin = true;
        const checkResult = await this.authService.checkLogin(username, password);
        this.checkingLogin = false;

        if (checkResult && checkResult.status === 'UPDATE_PASSWORD') {
          // User must update password - navigate to new password screen
          sessionStorage.setItem('temp_username', username);
          sessionStorage.setItem('temp_password', password);

          this.router.navigate(['/new-password'], {
            state: { username, password }
          });
          return;
        } else if (checkResult && checkResult.status === 'OK') {
          // Normal login - proceed with Keycloak authentication
          const success = await this.authService.login(username, password);
          if (success) {
            this.rateLimitService.recordSuccessfulLogin();
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Login successful'
            });

            // Handle redirect after successful login
            if (this.redirectUrl) {
              // Decode the redirect URL and navigate
              const decodedRedirectUrl = decodeURIComponent(this.redirectUrl);
              this.router.navigateByUrl(decodedRedirectUrl);
            } else {
              this.router.navigate(['/']);
            }
          } else {
            this.rateLimitService.recordFailedAttempt();
            if (this.authService.loginFailReason !== 'ACCOUNT_DEACTIVATED') {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Invalid credentials'
              });
            }
          }
        } else {
          // Error response from check-login
          this.rateLimitService.recordFailedAttempt();
          this.messageService.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: checkResult?.message || 'Invalid credentials',
            life: 5000
          });
        }
      } catch (error: any) {
        this.handleLoginError(error);
      } finally {
        this.loading = false;
      }
    }
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  calculatePasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = { score: 0, feedback: '', color: '' };
      return;
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Determine strength
    if (score < 3) {
      this.passwordStrength = {
        score,
        feedback: 'Weak',
        color: '#ef4444'
      };
    } else if (score < 5) {
      this.passwordStrength = {
        score,
        feedback: 'Medium',
        color: '#f59e0b'
      };
    } else {
      this.passwordStrength = {
        score,
        feedback: 'Strong',
        color: '#10b981'
      };
    }
  }

  private handleLoginError(error: any) {
    this.rateLimitService.recordFailedAttempt();

    let errorMessage = 'Login failed. Please try again.';
    let severity: 'error' | 'warn' = 'error';

    if (error?.status === 401) {
      errorMessage = 'Invalid username or password.';
    } else if (error?.status === 429) {
      errorMessage = 'Too many requests. Please wait and try again.';
      severity = 'warn';
    } else if (error?.status === 0) {
      errorMessage = 'Network error. Please check your connection.';
    } else if (error?.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    this.messageService.add({
      severity,
      summary: 'Login Failed',
      detail: errorMessage,
      life: 5000
    });
  }

  get isFormDisabled(): boolean {
    return this.loading || this.checkingLogin || this.rateLimitService.isLockedOut();
  }

  goToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }
}
